import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

//Function makes sure any given value has the right spaces
//e.g. "  Ana   Stanković\n\tZagreb  " → " Ana Stanković Zagreb "
function normalize(value) {
  return (value || "").toString().replace(/\s+/g, " ").trim();
}

function normalizeKey(value) {
  return normalize(value).toLowerCase();
}

function parseFieldBlock(text) {
  const result = {};
  if (!text) return result;

  String(text)
    .split(";")
    .map((chunk) => chunk.replace(/\n/g, " ").trim())
    .filter(Boolean)
    .forEach((chunk) => {
      const colonIndex = chunk.indexOf(":");
      if (colonIndex === -1) return;
      const label = chunk.slice(0, colonIndex).trim();
      const value = chunk.slice(colonIndex + 1).trim();
      if (label) result[label] = value;
    });

  return result;
}

//Birth dates come from free text in the pet cell, so accept the common
//variants (e.g. "12.03.2019.", "12. 3. 2019.", "12/03/2019", "2019-03-12")
const BIRTH_DATE_FORMATS = [
  "DD.MM.YYYY.",
  "D.M.YYYY.",
  "DD.MM.YYYY",
  "D.M.YYYY",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "YYYY-MM-DD",
];

export function parseImportedBirthDate(value) {
  if (!value) return null;
  const cleaned = String(value).replace(/\s+/g, "");
  const parsed = dayjs(cleaned, BIRTH_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function excelSerialToDate(serial) {
  if (typeof serial !== "number" || Number.isNaN(serial)) return null;
  const utcDays = Math.floor(serial - 25569);
  const utcMs = utcDays * 86400 * 1000;
  const date = new Date(utcMs);
  const fractional = serial - Math.floor(serial);
  date.setMilliseconds(
    date.getMilliseconds() + Math.round(fractional * 86400 * 1000),
  );
  return date;
}

const COLUMNS = {
  ROW_NUMBER: 0,
  INVOICE_DATE: 1,
  INVOICE_NUMBER: 4,
  ISSUER: 5,
  OWNER: 7,
  PET: 8,
  SERVICE: 9,
  MATERIALS: 12,
  TOTAL: 15,
};

const HEADER_ROW_LABEL = "Rbr.";

function findHeaderRowIndex(rows) {
  for (let i = 0; i < rows.length; i++) {
    if (normalize(rows[i]?.[0]) === HEADER_ROW_LABEL) return i;
  }
  return -1;
}

function extractOwner(ownerCell) {
  const fields = parseFieldBlock(ownerCell);
  return {
    name: normalize(fields["Kupac"]),
    oib: normalize(fields["OIB"]),
    address: normalize(fields["Adresa"]),
  };
}

function extractPet(
  petCell,
  {
    rowNumber,
    invoiceNumber,
    invoiceDate,
    serviceCell,
    materialsCell,
    totalCell,
  },
) {
  const fields = parseFieldBlock(petCell);
  const serviceFields = parseFieldBlock(serviceCell);

  return {
    name: normalize(fields["Ime životinje"]) || "(bez imena)",
    species: normalize(fields["Vrsta životinje"]),
    breed: normalize(fields["Pasmina"]),
    birthDate: normalize(fields["Datum rođenja"]),
    color: normalize(fields["Boja"]),
    microchip: normalize(fields["Mikročip"]),
    passport: normalize(fields["Putovnica"]),
    //The export has no gender field - a pet counts as MALE unless changed to FEMALE in the edit dialog
    gender: "MALE",

    //LEAVE THIS IN FOR NOW, IT'S COMMENTED OUT BECAUSE IT AFFECT THE UNIQUE PET OBJECT FILTER
    //We might want something like this later on
    // visit: {
    //   rowNumber,
    //   invoiceNumber,
    //   invoiceDate: invoiceDate ? invoiceDate.toISOString() : null,
    //   anamnesis: normalize(serviceFields["Anamneza"]),
    //   diagnosis: normalize(serviceFields["Dijagnoza"]),
    //   services: normalize(serviceFields["Usluge"]),
    //   materials: normalize(materialsCell),
    //   total: normalize(totalCell),
    // },
  };
}

function isSameOwner(a, b) {
  return (
    normalizeKey(a.name) === normalizeKey(b.name) &&
    normalizeKey(a.oib) === normalizeKey(b.oib) &&
    normalizeKey(a.address) === normalizeKey(b.address)
  );
}

function ownerGroupKey(owner) {
  return `${normalizeKey(owner.name)}|${normalizeKey(owner.oib)}|${normalizeKey(owner.address)}`;
}

export function parseAmbulantniDnevnik(rows) {
  const headerIndex = findHeaderRowIndex(rows);
  if (headerIndex === -1) {
    throw new Error(
      "FileNotRecognized",
    );
  }

  const dataRows = rows.slice(headerIndex + 1);
  const groups = new Map();

  for (const row of dataRows) {
    const ownerCell = row[COLUMNS.OWNER];
    const petCell = row[COLUMNS.PET];
    if (!ownerCell || !petCell) continue;

    const owner = extractOwner(ownerCell);
    if (!owner.name) continue;

    const key = ownerGroupKey(owner);
    let group = groups.get(key);

    if (!group) {
      group = {
        id: key,
        name: owner.name,
        oib: owner.oib,
        address: owner.address,
        pets: [],
      };
      groups.set(key, group);
    } else if (!isSameOwner(group, owner)) {
      continue;
    }

    const invoiceDate = excelSerialToDate(row[COLUMNS.INVOICE_DATE]);

    group.pets.push(
      extractPet(petCell, {
        rowNumber: row[COLUMNS.ROW_NUMBER],
        invoiceNumber: normalize(row[COLUMNS.INVOICE_NUMBER]),
        invoiceDate,
        serviceCell: row[COLUMNS.SERVICE],
        materialsCell: row[COLUMNS.MATERIALS],
        totalCell: row[COLUMNS.TOTAL],
      }),
    );
    
    //REMOVING DUPLICATE PETS
    let uniqueArr = [...new Set(group?.pets?.map(JSON.stringify))].map(
      JSON.parse,
    );

    group.pets = uniqueArr
  }


  return Array.from(groups.values()).map((group) => ({
    ...group,
    visitCount: group.pets.length,
  }));
}

export const __private__ = {
  normalize,
  normalizeKey,
  parseFieldBlock,
  excelSerialToDate,
  isSameOwner,
  ownerGroupKey,
  findHeaderRowIndex,
};
